import { SupabaseClient } from '@supabase/supabase-js';

// Helper to generate unique IDs (inlined to avoid dependency on UI hooks)
const genUniqueId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export interface CloneResult {
  success: boolean;
  groupId?: string;
  error?: any;
}

/**
 * Clones a published travel post into a new editable itinerary (group).
 */
export async function cloneTravelPostToItinerary(
  postId: string,
  currentUserId: string,
  supabaseClient: SupabaseClient,
  options?: {
    title?: string;
    startDate?: Date;
  }
): Promise<CloneResult> {
  try {
    // 1. Fetch the travel post
    const { data: post, error: postError } = await supabaseClient
      .from('travel_posts')
      .select('*')
      .eq('id', postId)
      .single();

    if (postError || !post) {
      throw new Error(postError?.message || 'Post not found');
    }

    // 2. Parse content_json (snapshot)
    let snapshot: any = post.content_json;
    if (typeof snapshot === 'string') {
      try {
        snapshot = JSON.parse(snapshot);
      } catch (e) {
        throw new Error('Invalid post content format');
      }
    }

    if (!snapshot || !snapshot.days || !Array.isArray(snapshot.days)) {
      throw new Error('Post content is not a valid itinerary snapshot');
    }

    // 3. Create a new group (itinerary)
    const newGroupPayload = {
      name: options?.title || `Copy of ${post.title}`,
      location: post.city || snapshot.city || null,
      background_image_url: post.cover_image || snapshot.coverImage || null,
      creator_id: currentUserId,
      status: 'active',
      visibility: 'private',
      allow_team_edit: false,
      itinerary_version: 1,
      itinerary_summary: {}, // Initial empty summary
    };

    const { data: newGroup, error: groupError } = await supabaseClient
      .from('groups')
      .insert([newGroupPayload])
      .select()
      .single();

    if (groupError || !newGroup) {
      throw new Error(groupError?.message || 'Failed to create new itinerary');
    }

    const newGroupId = newGroup.id;

    // 3.5 Add creator as member (CRITICAL for visibility in getMyTravels)
    const { error: memberError } = await supabaseClient
      .from('group_members')
      .insert({
        group_id: newGroupId,
        user_id: currentUserId,
        role: 'creator',
        share_ratio: 1.0,
        status: 'active',
      });

    if (memberError) {
      console.error('Failed to add creator as member', memberError);
      throw new Error('Failed to add creator as member');
    }

    // 4. Insert itinerary entries
    const entriesToInsert: any[] = [];

    snapshot.days.forEach((day: any, dayIndex: number) => {
      const dayNumber = dayIndex + 1;
      
      // Calculate date if startDate is provided
      let dayDate: string | null = null;
      if (options?.startDate) {
        const d = new Date(options.startDate);
        d.setDate(d.getDate() + dayIndex);
        dayDate = d.toISOString().split('T')[0];
      }

      if (day.entries && Array.isArray(day.entries)) {
        day.entries.forEach((entry: any, entryIndex: number) => {
          // Skip hidden entries if any (though snapshot usually filters them out, good to be safe)
          if (entry.hidden) return;

          // Map snapshot entry to DB row
          // Note: We strip specific IDs to ensure new entries are created
          
          // Combine notes into a single string if present
          let noteText = null;
          if (entry.notes && Array.isArray(entry.notes) && entry.notes.length > 0) {
            noteText = entry.notes.map((n: any) => n.text).join('\n\n');
          } else if (entry.description) {
            noteText = entry.description;
          }

          // Resolve item type
          // Snapshot types: 'card', 'note', etc.
          // DB types: 'card', 'note', 'transport', 'custom'
          
          const meta = entry.meta || {};
          const cardId = meta.card_id || entry.cardId || null;
          let itemType = 'custom';

          if (cardId) {
            itemType = 'card';
          } else if (entry.type === 'note') {
            itemType = 'note';
          } else if (entry.type === 'transport') {
            itemType = 'transport';
          } else {
            // For other types (poi, restaurant, etc.) without card_id, treat as custom
            itemType = 'custom';
          }

          // Prepare custom_payload
          // We want to preserve the snapshot's payload as the starting point for the new entry
          const customPayload = entry.payload || entry.custom_payload || {};

          // Extract time from various possible locations in snapshot
          // Priority: meta.start_time > meta.time > entry.time > entry.startTime
          const startTime = meta.start_time || meta.time || entry.time || entry.startTime || null;

          const row = {
            group_id: newGroupId,
            day_number: dayNumber,
            day_date: dayDate,
            sort_order: entryIndex,
            item_type: itemType,
            card_id: cardId,
            note: noteText,
            start_time: startTime,
            end_time: null,
            custom_payload: customPayload,
            inline_payload: customPayload, // Duplicate for safety as some logic uses inline
            creator_id: currentUserId,
          };

          entriesToInsert.push(row);
        });
      }
    });

    if (entriesToInsert.length > 0) {
      const { error: insertError } = await supabaseClient
        .from('group_itinerary_entries')
        .insert(entriesToInsert);

      if (insertError) {
        // If bulk insert fails, we might want to delete the group or just throw
        console.error('Failed to insert entries', insertError);
        throw new Error('Failed to populate itinerary entries');
      }
    }

    return { success: true, groupId: newGroupId };

  } catch (error) {
    console.error('cloneTravelPostToItinerary failed:', error);
    return { success: false, error };
  }
}
